import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { set } from '@ember/object';
import { on } from '@ember/modifier';
import { concat, fn } from '@ember/helper';
import { task } from 'ember-concurrency';
import { or, and, not, gt } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { pick, queue } from '@nullvoxpopuli/ember-composable-helpers';
import setHelper from 'ember-set-helper/helpers/set';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import {
  HdsPageHeader,
  HdsAlert,
  HdsTextDisplay,
  HdsFormLabel,
  HdsFormTextInputBase,
  HdsButtonSet,
  HdsButton,
  HdsFormError,
  HdsSeparator,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  FlexGrid,
  WithErrors,
  StickyFooter,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';
import BasicRoleSelect from 'hcp/components/iam/invitations/form/basic-role-select';
import NoRoleWithProjects from 'hcp/components/iam/invitations/alert/no-role-with-projects';

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~\-\u0080-\uFFFF]+@[a-zA-Z0-9-\u0080-\uFFFF]+(\.[a-zA-Z0-9-\u0080-\uFFFF]+)*\.[a-zA-Z\u0080-\uFFFF]{1,}$/u;

const EXISTING_EMAIL_ERROR_MESSAGE =
  'user principals with following emails are already members: ';
const EXISTING_PENDING_EMAIL_ERROR_MESSAGE = 'user invites already exist';

function findDuplicates(users) {
  const seenEmails = new Set();
  const duplicates = [];

  users.forEach(([user, index]) => {
    if (seenEmails.has(user.inviteeEmail)) {
      duplicates.push([user, index]);
    } else {
      seenEmails.add(user.inviteeEmail);
    }
  });

  return duplicates;
}

class Invitee {
  @tracked inviteeEmail;
  @tracked role;
  @tracked isPristine = true;

  constructor({ inviteeEmail, role, isPristine = true }) {
    this.inviteeEmail = inviteeEmail;
    this.role = role;
    this.isPristine = isPristine;
    // `fieldId` is explictly used to track field errors associated with an Invitee
    this.fieldId = crypto.randomUUID();
  }
}

/**
 * @argument organization
 * @type {Object}
 */

/**
 * @description A complex regular expression converted from the API's GO regex
 * to ensure synchronization between the UI and API validation logic. This regex
 * is employed for client-side validation during typing. Corresponding API tests
 * are incorporated in the integration test file of this component to maintain
 * consistency.
 * @see {@link https://github.com/asaskevich/govalidator/blob/a9d515a09cc289c60d55064edec5ef189859f172/patterns.go#L7}
 * for the original Regex used by the API at the time of this comment.
 */
/**
 *
 * `PageUsersInvite` displays the create form for inviting a HCP User.
 *
 *
 * ```
 * <Page::Invites::Invite @organization={{this.model.organization}} />
 * ```
 *
 * @class PageUsersInvite
 *
 */
export default class PageUsersInvite extends Component {
  @service api;
  @service abilities;
  @service flashMessages;
  @service intl;
  @service router;
  @service userContext;

  @tracked users = [];
  @tracked validationError;
  @tracked showRoleDefinitionsFlyout = false;

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  addEmptyUser = () => {
    this.users = [...this.users, new Invitee({ inviteeEmail: '', role: '' })];
  };

  removeUserByIndex = (indexToRemove) => {
    this.users = [
      ...this.users.slice(0, indexToRemove),
      ...this.users.slice(indexToRemove + 1),
    ];
  };

  findExistingUserIndices(existingMemberEmails, errorType) {
    const { users } = this;

    return users
      .map((user, index) => [user, index])
      .filter(([user]) => {
        if (errorType === 'invite') {
          return existingMemberEmails.some(
            (member) => member.description === user.inviteeEmail
          );
        } else {
          return existingMemberEmails.includes(user.inviteeEmail);
        }
      });
  }

  async displayExistingEmails(...args) {
    const [users, errorType] = args;

    const validationError = new Error();
    validationError.details = [];

    // Find existing org users and/or existing pending invitations
    const existingUsers = this.findExistingUserIndices(users, errorType);

    if (existingUsers.length) {
      validationError.details = [
        ...validationError.details,
        {
          field_violations: [
            ...existingUsers.map(([user]) => {
              return {
                field: `inviteeEmail[${user.fieldId}]`,
                description: this.intl.t(
                  `components.page.access-control.users.invite.error.${errorType}-exists`
                ),
              };
            }),
          ],
        },
      ];
    }

    if (validationError.details.length) {
      throw validationError;
    }
  }

  validate = task(async () => {
    const { users } = this;

    try {
      const validationError = new Error();
      validationError.details = [];

      const userTuples = users.map((user, index) => {
        return [user, index];
      });

      // Find empty emails addresses.
      const usersWithEmptyEmail = userTuples.filter(([user]) => {
        return !user.inviteeEmail;
      });

      // Find invalid emails addresses.
      const usersWithInvalidEmail = userTuples.filter(([user]) => {
        return user.inviteeEmail && !EMAIL_REGEX.test(user.inviteeEmail);
      });

      // Find duplicate emails addresses.
      const duplicatedUsers = findDuplicates(userTuples);

      if (usersWithEmptyEmail.length) {
        validationError.details = [
          ...validationError.details,
          ...usersWithEmptyEmail.map(([user]) => {
            return {
              field_violations: [
                {
                  field: `inviteeEmail[${user.fieldId}]`,
                  description: this.intl.t(
                    'components.page.access-control.users.invite.form.cannot-be-blank'
                  ),
                },
              ],
            };
          }),
        ];
      }

      if (usersWithInvalidEmail.length) {
        validationError.details = [
          ...validationError.details,
          ...usersWithInvalidEmail.map(([user]) => {
            return {
              field_violations: [
                {
                  field: `inviteeEmail[${user.fieldId}]`,
                  description: this.intl.t(
                    'components.page.access-control.users.invite.form.enter-a-valid-email-address'
                  ),
                },
              ],
            };
          }),
        ];
      }

      if (duplicatedUsers.length) {
        validationError.details = [
          ...validationError.details,
          ...duplicatedUsers.map(([user]) => {
            return {
              field_violations: [
                {
                  field: `inviteeEmail[${user.fieldId}]`,
                  description: this.intl.t(
                    'components.page.access-control.users.invite.form.cannot-invite-two-users-with-same-email'
                  ),
                },
              ],
            };
          }),
        ];
      }

      if (validationError.details.length) {
        throw validationError;
      } else {
        this.validationError = undefined;
      }
    } catch (e) {
      this.validationError = e;
    }
  });

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();

    const { organization } = this.args;
    const { users } = this;

    // Simulate a touch event to expose all inline errors.
    users.map((user) => {
      set(user, 'isPristine', false);
      return user;
    });

    if (users.length == 0) {
      throw new Error('You must invite at least one user');
    }

    await this.validate.perform();

    if (this.validationError) {
      return;
    }

    const payload = {
      invitations: users.map(({ inviteeEmail, role }) => {
        return { inviteeEmail, role };
      }),
    };

    try {
      await this.api.invitation.invitationsServiceCreateOrganizationInvitations(
        organization.id,
        payload
      );

      this.flashMessages.success(
        this.intl.t(
          'components.page.access-control.users.invite.success.users-invited',
          {
            numUsers: users.length,
          }
        ),
        {
          content: this.intl.t(
            'components.page.access-control.users.invite.success.you-may-grant-project-level-roles'
          ),
          linkText: this.intl.t(
            'components.page.access-control.users.invite.success.learn-more-about-project-roles'
          ),
          linkIcon: 'external-link',
          linkUrl:
            'https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/users#project-role',
        }
      );

      this.router.transitionTo('cloud.access-control.invites.list');
    } catch (e) {
      const error = typeof e.json === 'function' ? await e.json() : e;
      if (error) {
        if (error.message?.includes(EXISTING_EMAIL_ERROR_MESSAGE)) {
          const membersString = error.message.replace(
            EXISTING_EMAIL_ERROR_MESSAGE,
            ''
          );
          const existingMemberEmails = membersString.split(',');
          await this.displayExistingEmails(existingMemberEmails, 'user');
        }
        if (error.message?.includes(EXISTING_PENDING_EMAIL_ERROR_MESSAGE)) {
          const existingPendingMemberEmails = error.details[0].violations;
          await this.displayExistingEmails(
            existingPendingMemberEmails,
            'invite'
          );
        } else {
          this.onInviteError();
        }
      } else {
        this.onInviteError();
      }
    }
  });

  onInviteError() {
    this.flashMessages.error(
      this.intl.t(
        'components.page.access-control.users.invite.error.sending-invitations-failed'
      ),
      {
        content: this.intl.t(
          'components.page.access-control.users.invite.error.something-went-wrong'
        ),
        linkIcon: 'help',
        linkIconPosition: 'leading',
        linkText: this.intl.t(
          'components.page.access-control.users.invite.error.contact-support'
        ),
        linkUrl: this.supportTicketHref,
      }
    );
  }

  get noRoleUsers() {
    return this.users.filter((invitee) => {
      return !invitee.role;
    });
  }
  <template>
    <WithErrors
      @error={{or this.validationError this.save.last.error}}
      as |FormError FieldError|
    >
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.IconTile @icon="user" @iconSecondary="plus" />
            <PH.Title>
              {{t "components.page.access-control.users.invite.invite-users"}}
            </PH.Title>
          </HdsPageHeader>
        </:header>
        <:alert>
          {{#if this.userContext.organizationMeta.unifiedExperienceEnabled}}
            <FlexGrid as |G|>
              <G.Item @xs="12" @md="6">
                <HdsAlert
                  @type="compact"
                  data-test-invite-users-hcp-terraform-alert
                  as |A|
                >
                  <A.Description>{{t
                      "components.page.access-control.users.invite.setting-roles-and-permissions"
                    }}</A.Description>
                </HdsAlert>
              </G.Item>
            </FlexGrid>
          {{/if}}
          {{#if this.save.last.error}}
            <FormError />
          {{/if}}
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @md="6">
              <form
                class="hcp-hds-form"
                data-test-invite-users-add-role-form
                novalidate
                {{on "submit" this.save.perform}}
                {{didInsert this.addEmptyUser}}
              >
                <Flex @direction="column" @gap="xl">
                  <section>
                    <Flex
                      @direction="column"
                      @gap="xl"
                      data-test-invite-users-users-container
                    >
                      <Flex
                        @direction="column"
                        @gap="xs"
                        data-test-invite-users-messaging
                      >
                        <HdsTextDisplay
                          @tag="h2"
                          @size="300"
                          @weight="semibold"
                        >
                          {{t
                            "components.page.access-control.users.invite.form.add-users-to-org"
                          }}
                        </HdsTextDisplay>
                      </Flex>
                      <Flex
                        @direction="column"
                        @gap="sm"
                        data-test-invite-users-users-list
                      >
                        {{#let this.users as |users|}}
                          {{#each users as |user index|}}
                            <Flex
                              @direction="column"
                              @gap="xl"
                              data-test-invite-users-user-row
                            >
                              <Flex
                                @direction="column"
                                @gap="xs"
                                data-field-error={{concat
                                  "inviteeEmail["
                                  user.fieldId
                                  "]"
                                }}
                              >
                                <FieldError
                                  @field={{concat
                                    "inviteeEmail["
                                    user.fieldId
                                    "]"
                                  }}
                                  @hdsDelegatedError={{true}}
                                  as |err|
                                >
                                  {{#let
                                    (and (not user.isPristine) err)
                                    as |isInvalid|
                                  }}
                                    <Flex
                                      @direction="row"
                                      @grow={{1}}
                                      @shrink={{1}}
                                      @basis="zero-percent"
                                    >
                                      <HdsFormLabel
                                        @controlId={{concat
                                          "user-email-error-"
                                          index
                                        }}
                                        for={{concat "user-email-error-" index}}
                                        data-test-invite-users-user-email-label
                                      >
                                        {{t
                                          "components.page.access-control.users.invite.form.email-address-for-invitee"
                                        }}
                                      </HdsFormLabel>
                                    </Flex>
                                    <Flex
                                      @alignItems="start"
                                      @direction="row"
                                      @justifyContent="end"
                                      @gap="md"
                                    >
                                      <Flex
                                        @direction="row"
                                        @grow={{1}}
                                        @shrink={{1}}
                                        @basis="zero-percent"
                                      >
                                        <HdsFormTextInputBase
                                          @type="email"
                                          @value={{user.inviteeEmail}}
                                          @isRequired={{true}}
                                          @width="100%"
                                          @isInvalid={{isInvalid}}
                                          id={{concat
                                            "user-email-error-"
                                            index
                                          }}
                                          data-test-invite-users-user-email-input
                                          {{on
                                            "blur"
                                            (setHelper user "isPristine" false)
                                          }}
                                          {{on
                                            "input"
                                            (queue
                                              (pick
                                                "target.value"
                                                (setHelper user "inviteeEmail")
                                              )
                                              this.validate.perform
                                            )
                                          }}
                                        />
                                      </Flex>
                                      {{#if (gt users.length 1)}}
                                        <HdsButtonSet>
                                          <HdsButton
                                            @color="tertiary"
                                            @icon="user-minus"
                                            @text={{t
                                              "components.page.access-control.users.invite.form.remove"
                                            }}
                                            type="button"
                                            data-test-invite-users-remove-user-button
                                            {{on
                                              "click"
                                              (fn this.removeUserByIndex index)
                                            }}
                                          />
                                        </HdsButtonSet>
                                      {{/if}}
                                    </Flex>
                                    {{#if isInvalid}}
                                      <HdsFormError
                                        @controlId={{concat
                                          "user-email-error-"
                                          index
                                        }}
                                        data-test-invite-users-validation-user-email-error={{index}}
                                        as |E|
                                      >
                                        <E.Message>
                                          {{err.description}}
                                        </E.Message>
                                      </HdsFormError>
                                    {{/if}}
                                  {{/let}}
                                </FieldError>
                              </Flex>
                              <Flex
                                @alignItems="start"
                                @direction="column"
                                @gap="xl"
                              >
                                <section>
                                  <BasicRoleSelect
                                    @roleId={{user.role}}
                                    @onChange={{pick
                                      "value"
                                      (setHelper user "role")
                                    }}
                                    data-test-user-edit-assignment-basic-role-select
                                  >
                                    <:afterSelect>
                                      <HdsButton
                                        @color="tertiary"
                                        @icon="help"
                                        @iconPosition="leading"
                                        @isInline={{true}}
                                        @text={{t
                                          "components.page.access-control.users.invite.form.what-can-each-role-do"
                                        }}
                                        {{on
                                          "click"
                                          (setHelper
                                            this
                                            "showRoleDefinitionsFlyout"
                                            true
                                          )
                                        }}
                                      />
                                    </:afterSelect>
                                  </BasicRoleSelect>
                                </section>
                              </Flex>
                            </Flex>
                            {{#if (gt users.length 1)}}
                              <HdsSeparator />
                            {{else}}
                              <div></div>
                            {{/if}}
                          {{/each}}
                        {{/let}}
                        <HdsButtonSet {{boxPadding "0 0 xl 0"}}>
                          <HdsButton
                            @color="tertiary"
                            @icon="user-plus"
                            @text={{t
                              "components.page.access-control.users.invite.form.add-another-user"
                            }}
                            type="button"
                            data-test-invite-users-add-another-user
                            {{on "click" this.addEmptyUser}}
                          />
                        </HdsButtonSet>
                      </Flex>
                    </Flex>
                  </section>
                  <StickyFooter>
                    <Flex @alignItems="center" @direction="row" @gap="md">
                      <HdsButtonSet>
                        <HdsButton
                          @color="primary"
                          @text={{t
                            "components.page.access-control.users.invite.invite"
                          }}
                          disabled={{this.save.isRunning}}
                          type="submit"
                          data-test-submit
                        />
                        <HdsButton
                          @color="secondary"
                          @route="cloud.access-control.users.list"
                          @text={{t "components.form.cancel"}}
                          data-test-cancel
                        />
                      </HdsButtonSet>
                      {{#if this.noRoleUsers.length}}
                        <NoRoleWithProjects
                          data-test-invite-users-no-role-alert
                        />
                      {{/if}}
                    </Flex>
                  </StickyFooter>
                </Flex>
              </form>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
      {{#if this.showRoleDefinitionsFlyout}}
        <RoleDefinitions
          @rolesLocation={{@currentRolesLocation}}
          @currentScopeLink={{@currentScopeLink}}
          @onClose={{setHelper this "showRoleDefinitionsFlyout" false}}
        />
      {{/if}}
    </WithErrors>
  </template>
}
